import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { StripeService } from '../../services';
import styles from './styles.module.css';

const STRIPE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

const StripeCheckoutModal = ({
  isOpen,
  onClose,
  tier,
  userId,
  email,
  returnUrl,
  isGuest = false,
  checkoutType = 'subscription',
  packageCode,
  promotionCode,
  offerCode,
  campaignArtGenerationId,
}) => {
  const { i18n } = useTranslation();
  const [checkoutError, setCheckoutError] = useState(null);
  const fetchRequestIdRef = useRef(0);

  const locale = useMemo(() => {
    const currentLng = localStorage.getItem('i18nextLng') || i18n.language;

    if (currentLng && (currentLng.toLowerCase().includes('pt') || currentLng.toLowerCase().includes('br'))) {
      return 'pt-BR';
    }

    if (currentLng && currentLng.toLowerCase().includes('en')) {
      return 'en';
    }

    return 'auto';
  }, [i18n.language]);

  const stripePromise = useMemo(() => {
    return loadStripe(STRIPE_KEY, { locale });
  }, [locale]);

  const checkoutParamsRef = useRef({
    tier,
    userId,
    email,
    returnUrl,
    isGuest,
    checkoutType,
    packageCode,
    promotionCode,
    offerCode,
    campaignArtGenerationId,
  });

  checkoutParamsRef.current = {
    tier,
    userId,
    email,
    returnUrl,
    isGuest,
    checkoutType,
    packageCode,
    promotionCode,
    offerCode,
    campaignArtGenerationId,
  };

  const fetchClientSecret = useCallback(async () => {
    const requestId = fetchRequestIdRef.current + 1;
    fetchRequestIdRef.current = requestId;

    const params = checkoutParamsRef.current;

    try {
      let result;

      if (params.checkoutType === 'addon') {
        result = await StripeService.createAddonCheckoutSession({
          packageCode: params.packageCode,
          email: params.email || undefined,
          returnUrl: params.returnUrl || undefined,
          promotionCode: params.promotionCode || undefined,
          offerCode: params.offerCode || undefined,
          campaignArtGenerationId: params.campaignArtGenerationId || undefined,
        });
      } else if (params.isGuest) {
        result = await StripeService.createGuestCheckoutSession({
          tier: Number(params.tier),
          email: params.email || undefined,
          returnUrl: params.returnUrl || undefined,
        });
      } else {
        result = await StripeService.createCheckoutSession({
          tier: Number(params.tier),
          userId: params.userId,
          email: params.email || undefined,
          returnUrl: params.returnUrl || undefined,
          offerCode: params.offerCode || undefined,
        });
      }

      if (fetchRequestIdRef.current !== requestId) {
        throw new Error('Checkout cancelado');
      }

      if (result.responseStatus === 200 && result.data?.clientSecret) {
        setCheckoutError(null);
        return result.data.clientSecret;
      }

      const message =
        result.message
        || result.data?.error?.message
        || result.data?.message
        || 'Erro ao criar sessão de checkout';

      setCheckoutError(message);
      throw new Error(message);
    } catch (error) {
      if (fetchRequestIdRef.current === requestId) {
        const message = error?.message || 'Erro ao criar sessão de checkout';
        setCheckoutError(message);
      }

      console.error('Error fetching client secret:', error);
      throw error;
    }
  }, []);

  const checkoutKey = `${checkoutType}-${tier ?? 'none'}-${packageCode ?? 'none'}-${campaignArtGenerationId ?? 'none'}-${promotionCode ?? 'none'}-${offerCode ?? 'none'}`;

  useEffect(() => {
    if (isOpen) {
      setCheckoutError(null);
      fetchRequestIdRef.current += 1;
    }
  }, [isOpen, checkoutKey]);

  const options = useMemo(
    () => ({ fetchClientSecret }),
    [fetchClientSecret],
  );

  if (!isOpen) return null;

  const modal = (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <button type="button" className={styles.closeButton} onClick={onClose}>&times;</button>
        <div id="checkout" className={styles.checkoutWrapper}>
          {!STRIPE_KEY ? (
            <p className={styles.checkoutError}>
              Chave pública do Stripe não configurada (VITE_STRIPE_PUBLISHABLE_KEY).
            </p>
          ) : checkoutError ? (
            <p className={styles.checkoutError}>{checkoutError}</p>
          ) : (
            <EmbeddedCheckoutProvider
              key={checkoutKey}
              stripe={stripePromise}
              options={options}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modal, document.body);
  }

  return modal;
};

export default StripeCheckoutModal;
